import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import { classifyTransaction } from '@/lib/transactionClassifier';
import { smartGroupTransactions } from '@/lib/transactionGrouping';
import { filterDuplicateTransactions } from '@/lib/bankTransactionDuplicateDetection';
import { BankAccountSummaryDisplay, type TransactionStats } from './BankAccountSummaryDisplay';
import { cn } from '@/lib/utils';
import { AlertCircle, CheckCircle2, Upload, Link2, CreditCard, RefreshCw, Unlink, Settings, ArrowRight, Database, Calendar, Loader2, Landmark } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface TellerAccount {
  id: string;
  name: string;
  type: string;
  subtype?: string;
  last_four?: string;
  institution: {
    id: string;
    name: string;
  };
}

interface BankAccount {
  id: string;
  account_name: string;
  bank_name: string;
  account_type: string;
  connection_status: string;
  teller_enrollment_id?: string;
  teller_access_token?: string;
  teller_account_id?: string;
  teller_account_name?: string;
  teller_account_last_four?: string;
  teller_account_type?: string;
  teller_account_subtype?: string;
}

interface BankAccountsPhase2Step1Props {
  onNext: () => void;
  onAccountSelected: (accountId: string | null) => void;
  onLoadingStateChange?: (isLoading: boolean) => void;
}

// Bank Connection Overlay Component
function BankConnectionOverlay({ step }: { step: 'fetching' | 'selecting' }) {
  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-md z-[100] flex items-center justify-center">
      <Card className="p-8 max-w-md shadow-2xl border-2">
        <CardContent className="flex flex-col items-center gap-6">
          {/* Bank Icon */}
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Landmark className="h-8 w-8 text-primary" />
          </div>
          
          {/* Title */}
          <div className="text-center space-y-2">
            <h3 className="font-semibold text-xl">Connecting Your Bank</h3>
            <p className="text-sm text-muted-foreground">
              Please wait while we securely connect to your bank
            </p>
          </div>
          
          {/* Progress Steps */}
          <div className="w-full space-y-3 py-4">
            {/* Step 1: Verified */}
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span className="text-sm text-green-600 font-medium">Bank verified</span>
            </div>
            {/* Step 2: Fetching */}
            <div className="flex items-center gap-3">
              {step === 'fetching' ? (
                <Loader2 className="h-5 w-5 text-primary animate-spin" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              )}
              <span className={cn("text-sm", step === 'fetching' ? "text-foreground font-medium" : "text-green-600 font-medium")}>
                {step === 'fetching' ? 'Fetching your accounts...' : 'Accounts fetched'}
              </span>
            </div>
            {/* Step 3: Select */}
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 rounded-full border-2 border-muted" />
              <span className="text-sm text-muted-foreground">Select account to sync</span>
            </div>
          </div>
          
          {/* Time estimate */}
          <p className="text-xs text-muted-foreground">
            This usually takes 5-10 seconds
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export const BankAccountsPhase2Step1 = ({ onNext, onAccountSelected, onLoadingStateChange }: BankAccountsPhase2Step1Props) => {
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectingAccountId, setConnectingAccountId] = useState<string | null>(null);
  const [tellerConfig, setTellerConfig] = useState<{ applicationId: string; products: string[] } | null>(null);
  
  // State for pending account selection (accounts connected but need account selection)
  const [pendingSelectionAccountId, setPendingSelectionAccountId] = useState<string | null>(null);
  const [pendingSelectionAccounts, setPendingSelectionAccounts] = useState<TellerAccount[]>([]);
  const [showPendingSelectionModal, setShowPendingSelectionModal] = useState(false);
  const [fetchingAccounts, setFetchingAccounts] = useState(false);
  const [connectionStep, setConnectionStep] = useState<'fetching' | 'selecting' | null>(null);
  
  // Date range for import
  const [trainingWindowStart, setTrainingWindowStart] = useState<string>('');
  const [trainingWindowEnd, setTrainingWindowEnd] = useState<string>('');
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  
  // Import completion and summary state
  const [importCompleted, setImportCompleted] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [importStats, setImportStats] = useState<TransactionStats | null>(null);
  
  // Refreshing account details state
  const [refreshingAccountId, setRefreshingAccountId] = useState<string | null>(null);

  useEffect(() => {
    loadAccounts();
    loadTellerConnectScript();
    
    // Set default date range: last 1 month (changed from 3 months for faster testing)
    const now = new Date();
    const oneMonthAgo = new Date(now);
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const trainingStart = oneMonthAgo.toISOString().split('T')[0];
    const trainingEnd = now.toISOString().split('T')[0];
    
    setTrainingWindowStart(trainingStart);
    setTrainingWindowEnd(trainingEnd);
  }, []);

  const loadTellerConnectScript = () => {
    // Check if Teller Connect is already available
    if ((window as any).TellerConnect) {
      return;
    }

    // Check if script element already exists in DOM
    const existingScript = document.querySelector('script[src*="teller.io/connect/connect.js"]');
    if (existingScript) {
      return;
    }

    // Load Teller Connect script
    const script = document.createElement('script');
    script.src = 'https://cdn.teller.io/connect/connect.js';
    script.async = true;
    script.id = 'teller-connect-script';
    script.onload = () => {
      console.log('Teller Connect script loaded');
    };
    document.head.appendChild(script);
  };

  const loadAccounts = async () => {
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const { data, error } = await supabase
        .from('bank_accounts')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('created_at');

      if (error) throw error;
      setAccounts((data || []) as unknown as BankAccount[]);
    } catch (error: any) {
      console.error('Error loading accounts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load bank accounts',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectViaTeller = async (accountId: string) => {
    try {
      setConnectingAccountId(accountId);
      
      // Get Teller Connect config from edge function
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('teller-connect-config', {
        body: {},
      });

      if (response.error) {
        console.error('Teller Connect config error:', response.error);
        // Provide more helpful error messages
        if (response.error.message?.includes('not configured')) {
          throw new Error('Teller integration not configured. Please contact support.');
        }
        throw response.error;
      }
      
      if (!response.data?.applicationId) {
        console.error('Invalid response:', response.data);
        throw new Error('Failed to get Teller Connect configuration. Please check server logs.');
      }

      setTellerConfig({
        applicationId: response.data.applicationId,
        products: response.data.products || ['verify'],
      });
      onAccountSelected(accountId);
    } catch (error: any) {
      console.error('Error initiating Teller connection:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to initiate Teller connection',
        variant: 'destructive',
      });
      setConnectingAccountId(null);
    }
  };

  const handleManualUpload = async (accountId: string) => {
    try {
      const { error } = await supabase
        .from('bank_accounts')
        .update({ connection_status: 'manual_upload' } as any)
        .eq('id', accountId);

      if (error) throw error;

      await loadAccounts();
      onAccountSelected(accountId);
      // Automatically move to next step (Step 2) where they can upload files
      onNext();
    } catch (error: any) {
      console.error('Error setting manual upload:', error);
      toast({
        title: 'Error',
        description: 'Failed to update account',
        variant: 'destructive',
      });
    }
  };

  // Handle selecting account for pending_selection accounts (already connected but need account selection)
  const handleSelectAccountForPending = async (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    if (!account || !account.teller_access_token) {
      toast({
        title: 'Error',
        description: 'Account connection data not found. Please reconnect.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setFetchingAccounts(true);
      setPendingSelectionAccountId(accountId);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Call the save-enrollment function with access token to fetch accounts
      const response = await supabase.functions.invoke('teller-save-enrollment', {
        body: {
          accessToken: account.teller_access_token,
          enrollment: { 
            id: account.teller_enrollment_id,
            institution: { name: account.bank_name }
          },
          account_id: accountId,
          // Don't pass selected_teller_account_id to trigger account fetching
        },
      });

      if (response.error) throw response.error;

      if (response.data?.requires_selection && response.data?.accounts) {
        setPendingSelectionAccounts(response.data.accounts);
        setShowPendingSelectionModal(true);
      } else if (response.data?.success) {
        // Account was auto-selected (single account)
        toast({
          title: 'Success',
          description: 'Bank account linked successfully',
        });
        await loadAccounts();
        setPendingSelectionAccountId(null);
      }
    } catch (error: any) {
      console.error('Error fetching accounts for selection:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch accounts',
        variant: 'destructive',
      });
      setPendingSelectionAccountId(null);
    } finally {
      setFetchingAccounts(false);
    }
  };

  // Handle account selection from pending selection modal
  const handlePendingAccountSelect = async (tellerAccountId: string) => {
    const account = accounts.find(a => a.id === pendingSelectionAccountId);
    if (!account || !account.teller_access_token) {
      toast({
        title: 'Error',
        description: 'Account connection data not found',
        variant: 'destructive',
      });
      return;
    }

    // Find the selected Teller account to get all its details
    const selectedTellerAccount = pendingSelectionAccounts.find(a => a.id === tellerAccountId);

    try {
      setFetchingAccounts(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('teller-save-enrollment', {
        body: {
          accessToken: account.teller_access_token,
          enrollment: { 
            id: account.teller_enrollment_id,
            institution: { name: account.bank_name }
          },
          account_id: pendingSelectionAccountId,
          selected_teller_account_id: tellerAccountId,
          selected_teller_account_name: selectedTellerAccount?.name || null,
          selected_teller_account_last_four: selectedTellerAccount?.last_four || null,
          selected_teller_account_type: selectedTellerAccount?.type || null,
          selected_teller_account_subtype: selectedTellerAccount?.subtype || null,
        },
      });

      if (response.error) throw response.error;

      toast({
        title: 'Success',
        description: 'Bank account linked successfully',
      });

      setShowPendingSelectionModal(false);
      setPendingSelectionAccounts([]);
      setPendingSelectionAccountId(null);
      await loadAccounts();
    } catch (error: any) {
      console.error('Error saving account selection:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save account selection',
        variant: 'destructive',
      });
    } finally {
      setFetchingAccounts(false);
    }
  };

  // Handle unlinking Teller integration
  const handleUnlinkTeller = async (accountId: string) => {
    try {
      const { error } = await supabase
        .from('bank_accounts')
        .update({ 
          connection_status: 'not_connected',
          teller_enrollment_id: null,
          teller_access_token: null,
          teller_account_id: null,
          teller_account_name: null,
          sync_enabled: false,
        } as any)
        .eq('id', accountId);

      if (error) throw error;

      toast({
        title: 'Disconnected',
        description: 'Teller integration has been unlinked',
      });
      await loadAccounts();
    } catch (error: any) {
      console.error('Error unlinking Teller:', error);
      toast({
        title: 'Error',
        description: 'Failed to unlink Teller integration',
        variant: 'destructive',
      });
    }
  };

  // Handle refreshing account details from Teller (for accounts missing new fields)
  const handleRefreshAccountDetails = async (accountId: string) => {
    try {
      setRefreshingAccountId(accountId);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('teller-refresh-account-details', {
        body: { account_id: accountId },
      });

      if (response.error) throw response.error;

      if (response.data?.updated > 0) {
        toast({
          title: 'Details Refreshed',
          description: 'Account details have been updated from your bank',
        });
        await loadAccounts();
      } else if (response.data?.failed > 0) {
        toast({
          title: 'Refresh Failed',
          description: response.data.results?.[0]?.error || 'Could not refresh account details',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'No Changes',
          description: 'Account details are already up to date',
        });
      }
    } catch (error: any) {
      console.error('Error refreshing account details:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to refresh account details',
        variant: 'destructive',
      });
    } finally {
      setRefreshingAccountId(null);
    }
  };

  // Handle changing the linked Teller account - reconnect via Teller Connect to re-select
  const handleChangeTellerAccount = async (accountId: string) => {
    console.log('[handleChangeTellerAccount] Starting for account:', accountId);
    
    const account = accounts.find(a => a.id === accountId);
    if (!account) {
      toast({
        title: 'Error',
        description: 'Account not found. Please refresh the page.',
        variant: 'destructive',
      });
      return;
    }

    // Instead of fetching accounts via proxy (which may not be implemented),
    // trigger a new Teller Connect flow to re-authenticate and select account
    try {
      setConnectingAccountId(accountId);
      
      // Get Teller Connect config from edge function
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('teller-connect-config', {
        body: {},
      });

      if (response.error) {
        console.error('Teller Connect config error:', response.error);
        throw new Error(response.error.message || 'Failed to get Teller configuration');
      }
      
      if (!response.data?.applicationId) {
        throw new Error('Failed to get Teller Connect configuration');
      }

      // Set config to trigger TellerConnectComponent
      setTellerConfig({
        applicationId: response.data.applicationId,
        products: response.data.products || ['verify'],
      });
      onAccountSelected(accountId);
      
      toast({
        title: 'Reconnecting...',
        description: 'Please authenticate with your bank to select a different account.',
      });
    } catch (error: any) {
      console.error('[handleChangeTellerAccount] Error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to initiate account change',
        variant: 'destructive',
      });
      setConnectingAccountId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" />Connected</Badge>;
      case 'pending_selection':
        return <Badge className="bg-amber-500"><AlertCircle className="h-3 w-3 mr-1" />Select Account</Badge>;
      case 'manual_upload':
        return <Badge variant="outline"><Upload className="h-3 w-3 mr-1" />Manual Upload</Badge>;
      case 'failed':
        return <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="outline">Not Connected</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-6 w-64" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-9 w-32" />
                  <Skeleton className="h-9 w-24" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No bank accounts found. Please add bank accounts in Phase 1 first.
        </AlertDescription>
      </Alert>
    );
  }

  const allConnected = accounts.every(acc => 
    acc.connection_status === 'connected' || acc.connection_status === 'manual_upload'
  );
  
  // Check if any accounts need account selection
  const hasPendingSelection = accounts.some(acc => acc.connection_status === 'pending_selection');

  // Get connected accounts ready for import
  const connectedAccounts = accounts.filter(acc => 
    acc.connection_status === 'connected' && acc.teller_access_token && acc.teller_account_id
  );

  // Bulk import function
  const handleBulkImport = async () => {
    if (connectedAccounts.length === 0) {
      toast({
        title: 'No accounts ready',
        description: 'Please connect at least one account via Teller to import data',
        variant: 'destructive',
      });
      return;
    }

    if (!trainingWindowStart || !trainingWindowEnd) {
      toast({
        title: 'Date range required',
        description: 'Please select a date range for importing transactions',
        variant: 'destructive',
      });
      return;
    }

    try {
      setImporting(true);
      setImportProgress(0);
      
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      let totalTransactions = 0;
      let totalDuplicates = 0;
      const accountResults: Array<{ accountId: string; accountName: string; count: number; duplicates: number }> = [];

      // Import from each connected account
      for (let i = 0; i < connectedAccounts.length; i++) {
        const account = connectedAccounts[i];
        const accountProgress = (i / connectedAccounts.length) * 100;
        
        try {
          setImportProgress(Math.round(accountProgress + 5));

          const response = await supabase.functions.invoke('teller-fetch-transactions', {
            body: {
              account_id: account.id,
              start_date: trainingWindowStart,
              end_date: trainingWindowEnd,
            },
          });

          if (response.error) {
            console.error(`Error fetching transactions for ${account.account_name}:`, response.error);
            continue;
          }

          if (!response.data?.transactions || response.data.transactions.length === 0) {
            accountResults.push({
              accountId: account.id,
              accountName: account.account_name,
              count: 0,
              duplicates: 0,
            });
            continue;
          }

          setImportProgress(Math.round(accountProgress + 15));

          // Process transactions
          const transactions = response.data.transactions.map((tx: any) => {
            const txDate = new Date(tx.transaction_date);
            const trainingStartDate = new Date(trainingWindowStart);
            const trainingEndDate = new Date(trainingWindowEnd);

            let periodFlag = 'pre_ytd';
            if (txDate >= trainingStartDate && txDate <= trainingEndDate) {
              periodFlag = 'training_window';
            } else if (txDate > trainingEndDate) {
              periodFlag = 'ytd';
            }

            return {
              ...tx,
              period_flag: periodFlag,
              source: 'bank', // Track transaction origin for financial reporting
            };
          });

          setImportProgress(Math.round(accountProgress + 20));

          // Filter out duplicate transactions
          const { newTransactions, duplicateCount } = await filterDuplicateTransactions<typeof transactions[0]>(
            transactions,
            supabase
          );

          totalDuplicates += duplicateCount;

          if (newTransactions.length === 0) {
            accountResults.push({
              accountId: account.id,
              accountName: account.account_name,
              count: 0,
              duplicates: duplicateCount,
            });
            continue;
          }

          setImportProgress(Math.round(accountProgress + 25));

          // Classify and insert transactions in batches
          const batchSize = 50;
          for (let j = 0; j < newTransactions.length; j += batchSize) {
            const batch = newTransactions.slice(j, j + batchSize);
            
            const classificationPromises = batch.map(async (tx) => {
              try {
                if (tx.period_flag === 'training_window') {
                  return {
                    ...tx,
                    category_type: null,
                    auto_categorized: false,
                    needs_review: true,
                  };
                }

                const classification = await classifyTransaction({
                  id: `temp-${i}-${j}-${batch.indexOf(tx)}`,
                  transaction_date: tx.transaction_date,
                  amount: tx.amount,
                  description: tx.description || '',
                  merchant_name: tx.merchant_name,
                  account_id: account.id,
                  company_id: companyId,
                  location_id: undefined,
                });
                
                return {
                  ...tx,
                  category_type: classification.category_type,
                  subcategory: classification.subcategory,
                  detail_source: classification.detail_source,
                  classification_confidence: classification.confidence,
                  classification_method: classification.method,
                  auto_categorized: classification.confidence >= 0.8,
                  needs_review: classification.needsReview || classification.confidence < 0.8,
                };
              } catch (error) {
                console.error('Error classifying transaction:', error);
                return tx;
              }
            });
            
            const classifiedBatch = await Promise.all(classificationPromises);
            
            const { error } = await supabase
              .from('bank_transactions')
              .insert(classifiedBatch);

            if (error) {
              console.error('Error inserting batch:', error);
            }
            
            const batchProgress = (j + batchSize) / newTransactions.length;
            setImportProgress(Math.round(accountProgress + 25 + (batchProgress * 60)));
          }

          totalTransactions += newTransactions.length;
          accountResults.push({
            accountId: account.id,
            accountName: account.account_name,
            count: newTransactions.length,
            duplicates: duplicateCount,
          });

          // Update training progress
          await supabase
            .from('bank_account_training_progress')
            .upsert({
              company_id: companyId,
              account_id: account.id,
              financial_year_start: trainingWindowStart,
              training_window_start: trainingWindowStart,
              training_window_end: trainingWindowEnd,
              ytd_start: trainingWindowStart,
              transactions_imported: newTransactions.length,
              status: 'importing',
            }, {
              onConflict: 'company_id,account_id',
            });

        } catch (error) {
          console.error(`Error importing from ${account.account_name}:`, error);
        }
      }

      setImportProgress(100);

      // Provide detailed feedback
      if (totalTransactions === 0) {
        if (totalDuplicates > 0) {
          toast({
            title: 'No New Transactions',
            description: `All ${totalDuplicates} transaction${totalDuplicates > 1 ? 's' : ''} from the selected date range were already imported. Try selecting a different date range.`,
            variant: 'default',
          });
        } else {
          toast({
            title: 'No Transactions Found',
            description: `No transactions were found in the selected date range (${formatDisplayDate(trainingWindowStart)} to ${formatDisplayDate(trainingWindowEnd)}). Please try a different date range.`,
            variant: 'default',
          });
        }
      } else {
        const duplicateMessage = totalDuplicates > 0 
          ? ` (${totalDuplicates} duplicate${totalDuplicates > 1 ? 's' : ''} skipped)`
          : '';

        toast({
          title: 'Import Complete',
          description: `Imported ${totalTransactions} new transaction${totalTransactions !== 1 ? 's' : ''} from ${accountResults.length} account${accountResults.length > 1 ? 's' : ''}${duplicateMessage}`,
        });
      }

      // Load stats and show summary instead of moving to next step
      setImportCompleted(true);
      await loadImportStats();
    } catch (error: any) {
      console.error('Error in bulk import:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to import transactions',
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Load statistics for imported transactions
  const loadImportStats = async () => {
    try {
      setLoadingStats(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        setLoadingStats(false);
        return;
      }

      // Load uncategorized training window transactions
      // @ts-ignore - Supabase type inference issue
      const { data: transactionsData, error } = await supabase
        .from('bank_transactions')
        .select('*')
        .eq('company_id', companyId)
        .eq('period_flag', 'training_window')
        .is('category_type', null)
        .order('transaction_date', { ascending: false });
      
      if (error) throw error;

      if (!transactionsData || transactionsData.length === 0) {
        setImportStats({
          totalTransactions: 0,
          totalGroups: 0,
          revenueTransactions: 0,
          expenseTransactions: 0,
          revenueAmount: 0,
          expenseAmount: 0,
          accounts: [],
          accountDetails: {},
        });
        setLoadingStats(false);
        return;
      }

      // Load account details
      const accountIds = [...new Set(transactionsData.map((tx: any) => tx.account_id).filter((id: any): id is string => Boolean(id)))];
      const { data: accountsData } = await supabase
        .from('bank_accounts')
        .select('id, account_name, account_type')
        .in('id', accountIds);

      const accountMap = new Map((accountsData || []).map((acc: any) => {
        const accountName = acc.account_name?.toLowerCase() || '';
        // More precise credit card detection:
        // 1. Primary: account_type === 'credit'
        // 2. Secondary: Check for "credit card" phrase (not just "credit" which could match "Access")
        // 3. Check for " cc" or ends with " cc" (with word boundary)
        const isCreditCard = acc.account_type === 'credit' || 
                             /\bcredit\s+card\b/i.test(accountName) ||
                             /\bcc\b/i.test(accountName) ||
                             accountName.includes(' cc') ||
                             accountName.endsWith(' cc');
        
        return [acc.id, { 
          name: acc.account_name,
          type: acc.account_type,
          isCreditCard
        }];
      }));

      // Add account names and credit card flag to transactions
      interface Transaction {
        id: string;
        transaction_date: string;
        amount: number;
        description: string;
        merchant_name?: string;
        account_id?: string;
        account_name?: string;
        is_credit_card?: boolean;
        period_flag: string;
      }

      const transactions = transactionsData.map((tx: any) => {
        const accountInfo = accountMap.get(tx.account_id) || { name: 'Unknown Account', isCreditCard: false };
        return {
          ...tx,
          account_name: accountInfo.name,
          is_credit_card: accountInfo.isCreditCard,
        };
      }) as Transaction[];

      // Group transactions
      const isCombinedAccounts = true; // Always combined for bulk import
      const groups = smartGroupTransactions(transactions, isCombinedAccounts);

      // Calculate stats
      // IMPORTANT: is_credit_card flag on BANK ACCOUNT transactions means:
      // "This is a credit card PAYMENT transaction from the bank account"
      // These are balance_sheet transfers and should be excluded from revenue/expense
      // 
      // For regular bank accounts: positive = revenue (deposits), negative = expense (withdrawals)
      // Credit card payment transactions (is_credit_card=true) are always negative amounts
      // and represent money leaving the bank account to pay the credit card bill (balance_sheet)
      let revenueTransactions = 0;
      let expenseTransactions = 0;
      let revenueAmount = 0;
      let expenseAmount = 0;
      const accountDetails: Record<string, { name: string; count: number }> = {};

      // Debug tracking
      let excludedCCPayments = 0;
      let excludedCCAmount = 0;
      const debugLog: Array<{ account: string; isCC: boolean; amount: number; type: string }> = [];

      transactions.forEach(tx => {
        if (tx.is_credit_card) {
          // This is a CC payment from bank account → Balance sheet transfer
          // Skip entirely from revenue/expense calculations
          // (These are always negative amounts - money leaving bank to pay CC)
          excludedCCPayments++;
          excludedCCAmount += Math.abs(tx.amount);
          debugLog.push({
            account: tx.account_name || 'Unknown',
            isCC: true,
            amount: tx.amount,
            type: 'CC Payment (excluded)'
          });
        } else {
          // Regular bank account transaction
          if (tx.amount >= 0) {
            // Positive = Deposit/Revenue
            revenueTransactions++;
            revenueAmount += tx.amount;
            debugLog.push({
              account: tx.account_name || 'Unknown',
              isCC: false,
              amount: tx.amount,
              type: 'Revenue'
            });
          } else {
            // Negative = Withdrawal/Expense
            expenseTransactions++;
            const absAmount = Math.abs(tx.amount);
            expenseAmount += absAmount;
            debugLog.push({
              account: tx.account_name || 'Unknown',
              isCC: false,
              amount: tx.amount,
              type: 'Expense'
            });
          }
        }

        // Always count for account details regardless of type
        const accId = tx.account_id || 'unknown';
        if (!accountDetails[accId]) {
          accountDetails[accId] = {
            name: tx.account_name || 'Unknown Account',
            count: 0,
          };
        }
        accountDetails[accId].count++;
      });

      // Debug logging
      console.log('[loadImportStats] Revenue/Expense Calculation Summary:', {
        totalTransactions: transactions.length,
        revenueTransactions,
        revenueAmount: revenueAmount.toFixed(2),
        expenseTransactions,
        expenseAmount: expenseAmount.toFixed(2),
        excludedCCPayments,
        excludedCCAmount: excludedCCAmount.toFixed(2),
        sampleTransactions: debugLog.slice(0, 10) // Log first 10 for debugging
      });

      const accounts = Object.keys(accountDetails);

      setImportStats({
        totalTransactions: transactions.length,
        totalGroups: groups.length,
        revenueTransactions,
        expenseTransactions,
        revenueAmount,
        expenseAmount,
        accounts,
        accountDetails,
      });
    } catch (error: any) {
      console.error('Error loading stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load transaction statistics',
        variant: 'destructive',
      });
    } finally {
      setLoadingStats(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold mb-2">Import Transaction Data</h3>
        <p className="text-muted-foreground">
          Import transactions from all connected bank accounts. Select a date range and import data from all accounts at once.
        </p>
      </div>

      {/* Accounts List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold">Bank Accounts</h4>
          <div className="flex items-center gap-2">
            {/* Show refresh all button if any connected accounts are missing details */}
            {connectedAccounts.some(acc => acc.teller_account_id && !acc.teller_account_last_four) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  setRefreshingAccountId('all');
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session) throw new Error('Not authenticated');
                    const response = await supabase.functions.invoke('teller-refresh-account-details', {
                      body: {},
                    });
                    if (response.error) throw response.error;
                    if (response.data?.updated > 0) {
                      toast({
                        title: 'Details Refreshed',
                        description: `Updated ${response.data.updated} account(s) from your bank`,
                      });
                      await loadAccounts();
                    }
                  } catch (error: any) {
                    toast({
                      title: 'Error',
                      description: error.message || 'Failed to refresh accounts',
                      variant: 'destructive',
                    });
                  } finally {
                    setRefreshingAccountId(null);
                  }
                }}
                disabled={refreshingAccountId !== null}
              >
                <RefreshCw className={cn("h-4 w-4 mr-1", refreshingAccountId === 'all' && "animate-spin")} />
                {refreshingAccountId === 'all' ? 'Refreshing...' : 'Refresh All'}
              </Button>
            )}
            <Badge variant="outline">
              {connectedAccounts.length} ready to import
            </Badge>
          </div>
        </div>
        {accounts.map((account) => (
          <Card key={account.id}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-semibold">{account.account_name}</h4>
                    {getStatusBadge(account.connection_status || 'not_connected')}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {account.bank_name} • {account.account_type.replace('_', ' ')}
                  </p>
                  
                  {/* Show linked Teller account info or warning */}
                  {account.connection_status === 'connected' && account.teller_enrollment_id && (
                    <div className="mt-2">
                      {account.teller_account_id ? (
                        <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-md w-fit border border-green-200">
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5 text-sm">
                              <span className="text-muted-foreground">Syncing from:</span>
                              <span className="font-semibold text-foreground">{account.teller_account_name || 'Account'}</span>
                              {account.teller_account_last_four && (
                                <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                  ••••{account.teller_account_last_four}
                                </span>
                              )}
                            </div>
                            {(account.bank_name || account.teller_account_type) && (
                              <p className="text-xs text-muted-foreground">
                                {account.bank_name}
                                {account.bank_name && account.teller_account_type && ' • '}
                                {account.teller_account_subtype 
                                  ? account.teller_account_subtype.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                  : account.teller_account_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                }
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-md w-fit border border-amber-200">
                          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                          <span>No sub-account selected</span>
                          <span className="text-amber-500">— click Manage → Change Linked Account</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Connected accounts - show manage options or loading state */}
                  {account.connection_status === 'connected' && (
                    <>
                      {connectingAccountId === account.id ? (
                        <Button variant="outline" size="sm" disabled>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Connecting...
                        </Button>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Settings className="h-4 w-4 mr-2" />
                              Manage
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => handleRefreshAccountDetails(account.id)}
                              disabled={refreshingAccountId === account.id || connectingAccountId !== null}
                            >
                              <RefreshCw className={cn("h-4 w-4 mr-2", refreshingAccountId === account.id && "animate-spin")} />
                              {refreshingAccountId === account.id ? 'Refreshing...' : 'Refresh Details'}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleChangeTellerAccount(account.id)}
                              disabled={connectingAccountId !== null || refreshingAccountId !== null}
                            >
                              <CreditCard className="h-4 w-4 mr-2" />
                              Change Linked Account
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleUnlinkTeller(account.id)}
                              className="text-destructive focus:text-destructive"
                              disabled={refreshingAccountId !== null}
                            >
                              <Unlink className="h-4 w-4 mr-2" />
                              Unlink Integration
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </>
                  )}
                  
                  {/* Pending selection accounts - show select button */}
                  {account.connection_status === 'pending_selection' && (
                    <Button
                      size="sm"
                      onClick={() => handleSelectAccountForPending(account.id)}
                      disabled={fetchingAccounts && pendingSelectionAccountId === account.id}
                    >
                      {fetchingAccounts && pendingSelectionAccountId === account.id ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-4 w-4 mr-2" />
                          Select Account
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Not connected accounts - show connect options */}
                  {account.connection_status !== 'connected' && account.connection_status !== 'pending_selection' && account.connection_status !== 'manual_upload' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleManualUpload(account.id)}
                        disabled={connectingAccountId === account.id}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Manual Upload
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleConnectViaTeller(account.id)}
                        disabled={connectingAccountId === account.id}
                      >
                        <Link2 className="h-4 w-4 mr-2" />
                        Connect via Teller
                      </Button>
                    </>
                  )}
                  
                  {/* Manual upload accounts - show option to switch to Teller */}
                  {account.connection_status === 'manual_upload' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleConnectViaTeller(account.id)}
                      disabled={connectingAccountId === account.id}
                    >
                      <Link2 className="h-4 w-4 mr-2" />
                      Connect via Teller
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Date Range Selection */}
      {connectedAccounts.length > 0 && !importCompleted && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Select Date Range for Training
            </CardTitle>
            <CardDescription>
              Choose the date range for importing transactions. This will be your training window.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={trainingWindowStart}
                  onChange={(e) => setTrainingWindowStart(e.target.value)}
                  max={trainingWindowEnd || new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={trainingWindowEnd}
                  onChange={(e) => setTrainingWindowEnd(e.target.value)}
                  min={trainingWindowStart}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Import Button */}
      {connectedAccounts.length > 0 && !importCompleted && (
        <Card className="border-primary/30 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold mb-1">Import from All Connected Accounts</h4>
                  <p className="text-sm text-muted-foreground">
                    Import transactions from {connectedAccounts.length} connected account{connectedAccounts.length > 1 ? 's' : ''} for the selected date range
                  </p>
                </div>
              </div>
              
              <Button
                onClick={handleBulkImport}
                disabled={importing || !trainingWindowStart || !trainingWindowEnd}
                className="w-full h-12 text-base"
                size="lg"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Importing Transactions... {Math.round(importProgress)}%
                  </>
                ) : (
                  <>
                    <Database className="h-5 w-5 mr-2" />
                    Import Data from All Accounts
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
              
              {importing && (
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${importProgress}%` }}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Display after Import */}
      {importCompleted && (
        <>
          {loadingStats ? (
            <div className="space-y-6">
              <div className="space-y-2">
                <Skeleton className="h-7 w-64" />
                <Skeleton className="h-4 w-96" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardContent className="pt-6">
                      <Skeleton className="h-20 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Card>
                <CardContent className="pt-6">
                  <Skeleton className="h-40 w-full" />
                </CardContent>
              </Card>
            </div>
          ) : importStats && importStats.totalTransactions > 0 ? (
            <>
              <BankAccountSummaryDisplay stats={importStats} showGuidelines={true} />
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={onNext} size="lg">
                  Start Mapping Transactions
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </>
          ) : (
            <Alert className="border-amber-500 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>No transactions imported.</strong> The import completed but no transactions were found in the selected date range. Please try a different date range or ensure your accounts have transactions.
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {/* Only render Teller Connect component when we have config and account ID, and ensure only one instance */}
      {tellerConfig && connectingAccountId && (
        <TellerConnectComponent
          key={`teller-${connectingAccountId}`}
          config={tellerConfig}
          accountId={connectingAccountId}
          onConnectionStarted={() => {
            setConnectionStep('fetching');
            onLoadingStateChange?.(true);
          }}
          onSuccess={async () => {
            setConnectionStep(null);
            onLoadingStateChange?.(false);
            await loadAccounts();
            setTellerConfig(null);
            setConnectingAccountId(null);
            toast({
              title: 'Success',
              description: 'Bank account connected successfully',
            });
          }}
          onExit={() => {
            setConnectionStep(null);
            onLoadingStateChange?.(false);
            setTellerConfig(null);
            setConnectingAccountId(null);
          }}
          onAccountSelectionRequired={() => {
            // Keep loading state true - it will be hidden when modal opens
            // The loading overlay will be hidden when showAccountSelection becomes true
          }}
          onAccountSelectionModalOpen={() => {
            // Hide loading overlay when account selection modal opens
            setConnectionStep(null);
            onLoadingStateChange?.(false);
          }}
        />
      )}

      {/* Loading overlay when waiting for account selection after OTP */}
      {/* Keep overlay visible until account selection modal is confirmed open */}
      {connectionStep && !showPendingSelectionModal && (
        <BankConnectionOverlay step={connectionStep} />
      )}

      {/* Pending Selection Modal for accounts that need account selection */}
      <PendingAccountSelectionModal
        open={showPendingSelectionModal}
        accounts={pendingSelectionAccounts}
        saving={fetchingAccounts}
        onSelect={handlePendingAccountSelect}
        onClose={() => {
          setShowPendingSelectionModal(false);
          setPendingSelectionAccounts([]);
          setPendingSelectionAccountId(null);
        }}
      />

      {/* Alert for pending account selection */}
      {hasPendingSelection && (
        <Alert className="border-amber-500 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Action Required:</strong> Some accounts are connected but need you to select which bank account to sync. Click "Select Account" to complete the setup.
          </AlertDescription>
        </Alert>
      )}

    </div>
  );
};

// Pending Account Selection Modal Component
function PendingAccountSelectionModal({
  open,
  accounts,
  saving,
  onSelect,
  onClose,
}: {
  open: boolean;
  accounts: TellerAccount[];
  saving: boolean;
  onSelect: (accountId: string) => void;
  onClose: () => void;
}) {
  const getAccountIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'depository':
        return '🏦';
      case 'credit':
        return '💳';
      default:
        return '💰';
    }
  };

  const formatAccountType = (type: string, subtype?: string) => {
    if (subtype) {
      return subtype.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Account';
  };

  console.log('[PendingAccountSelectionModal] Rendering - open:', open, 'accounts:', accounts.length);
  
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !saving && onClose()}>
      <DialogContent className="sm:max-w-lg z-[100]">
        <DialogHeader className="space-y-3">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <CreditCard className="h-6 w-6 text-amber-600" />
          </div>
          <DialogTitle className="text-center text-xl">Select Bank Account</DialogTitle>
          <DialogDescription className="text-center">
            Your bank is connected! We found {accounts.length} account{accounts.length > 1 ? 's' : ''}.
            <br />
            <span className="font-medium text-foreground">Please select which account to use for transaction syncing:</span>
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 mt-4 max-h-[300px] overflow-y-auto pr-1">
          {accounts.map((account) => (
            <Card
              key={account.id}
              className={`cursor-pointer border-2 transition-all hover:border-primary hover:shadow-md ${saving ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => !saving && onSelect(account.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="text-2xl">
                    {getAccountIcon(account.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold truncate">{account.name}</h4>
                      {account.last_four && (
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                          ••••{account.last_four}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {account.institution?.name} • {formatAccountType(account.type, account.subtype)}
                    </p>
                  </div>
                  <CreditCard className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {saving && (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2">
            <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
            <span>Connecting account...</span>
          </div>
        )}
        
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Teller Connect Component Wrapper
function TellerConnectComponent({
  config,
  accountId,
  onSuccess,
  onExit,
  onAccountSelectionRequired,
  onAccountSelectionModalOpen,
  onConnectionStarted,
}: {
  config: { applicationId: string; products: string[] };
  accountId: string;
  onSuccess: () => void;
  onExit: () => void;
  onAccountSelectionRequired?: () => void;
  onAccountSelectionModalOpen?: () => void;
  onConnectionStarted?: () => void;
}) {
  const { toast } = useToast();
  const [showAccountSelection, setShowAccountSelection] = useState(false);
  const [tellerAccounts, setTellerAccounts] = useState<TellerAccount[]>([]);
  const [enrollmentData, setEnrollmentData] = useState<{ accessToken: string; enrollment: any } | null>(null);
  const [saving, setSaving] = useState(false);
  const tellerConnectInstanceRef = useRef<any>(null);
  const isInitializedRef = useRef(false);

  const saveEnrollment = useCallback(async (
    enrollment: { accessToken: string; enrollment: any }, 
    selectedTellerAccountId?: string, 
    selectedTellerAccount?: TellerAccount | null
  ) => {
    try {
      setSaving(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await supabase.functions.invoke('teller-save-enrollment', {
        body: {
          accessToken: enrollment.accessToken,
          enrollment: enrollment.enrollment,
          account_id: accountId,
          selected_teller_account_id: selectedTellerAccountId,
          selected_teller_account_name: selectedTellerAccount?.name || null,
          selected_teller_account_last_four: selectedTellerAccount?.last_four || null,
          selected_teller_account_type: selectedTellerAccount?.type || null,
          selected_teller_account_subtype: selectedTellerAccount?.subtype || null,
        },
      });

      if (response.error) throw response.error;

      // Check if account selection is required
      if (response.data?.requires_selection && response.data?.accounts) {
        console.log('Account selection required, accounts:', response.data.accounts);
        setTellerAccounts(response.data.accounts);
        setEnrollmentData(enrollment);
        setSaving(false);
        // Show account selection modal first
        setShowAccountSelection(true);
        // Then notify parent that account selection modal is now open (hide loading overlay)
        // Use setTimeout to ensure modal is rendered before hiding overlay
        setTimeout(() => {
          if (onAccountSelectionModalOpen) {
            onAccountSelectionModalOpen();
          }
        }, 50);
        return;
      }
      
      // If we get here but response indicates selection needed, log for debugging
      if (response.data?.requires_selection && !response.data?.accounts) {
        console.error('Backend requires selection but no accounts provided:', response.data);
        throw new Error('Account selection required but no accounts were returned');
      }

      toast({
        title: 'Success',
        description: 'Bank account connected successfully',
      });

      setShowAccountSelection(false);
      setEnrollmentData(null);
      onSuccess();
    } catch (error: any) {
      console.error('Error saving Teller enrollment:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to connect account',
        variant: 'destructive',
      });
      setSaving(false);
      onExit();
    }
  }, [accountId, onSuccess, onExit, toast]);

  const handleAccountSelect = async (tellerAccountId: string) => {
    if (!enrollmentData) return;
    // Find the selected account to get all its details
    const selectedAccount = tellerAccounts.find(a => a.id === tellerAccountId);
    await saveEnrollment(enrollmentData, tellerAccountId, selectedAccount || null);
  };

  useEffect(() => {
    // Prevent multiple instances - only create one Teller Connect instance per component mount
    if (isInitializedRef.current) {
      console.log('Teller Connect already initialized, skipping...');
      return;
    }

    const TellerConnect = (window as any).TellerConnect;
    
    if (!TellerConnect) {
      console.error('Teller Connect not loaded');
      toast({
        title: 'Error',
        description: 'Teller Connect script not loaded',
        variant: 'destructive',
      });
      onExit();
      return;
    }

    // Clean up any existing global instance first (in case of previous failed cleanup)
    // Teller Connect stores the instance globally, so we need to check for it
    if ((window as any).__tellerConnectInstance) {
      try {
        const existingInstance = (window as any).__tellerConnectInstance;
        if (typeof existingInstance.destroy === 'function') {
          existingInstance.destroy();
        } else if (typeof existingInstance.close === 'function') {
          existingInstance.close();
        }
      } catch (error) {
        console.warn('Error cleaning up global Teller Connect instance:', error);
      }
      delete (window as any).__tellerConnectInstance;
    }

    // Clean up our ref instance if it exists
    if (tellerConnectInstanceRef.current) {
      try {
        if (typeof tellerConnectInstanceRef.current.destroy === 'function') {
          tellerConnectInstanceRef.current.destroy();
        } else if (typeof tellerConnectInstanceRef.current.close === 'function') {
          tellerConnectInstanceRef.current.close();
        }
      } catch (error) {
        console.warn('Error cleaning up previous Teller Connect instance:', error);
      }
      tellerConnectInstanceRef.current = null;
    }

    try {
      const tellerConnect = TellerConnect.setup({
        applicationId: config.applicationId,
        products: config.products,
        // Connect ALL accounts, then show our own account selection modal
        // This allows us to fetch and display all accounts for user selection
        selectAccount: 'disabled',
        onSuccess: async (enrollment: any) => {
          // Immediately notify parent to show loading overlay
          if (onConnectionStarted) {
            onConnectionStarted();
          }
          const enrollmentData = {
            accessToken: enrollment.accessToken,
            enrollment: enrollment.enrollment || enrollment,
          };
          // Save enrollment - backend will fetch accounts and return them for selection
          await saveEnrollment(enrollmentData);
        },
        onExit: () => {
          isInitializedRef.current = false;
          tellerConnectInstanceRef.current = null;
          if ((window as any).__tellerConnectInstance) {
            delete (window as any).__tellerConnectInstance;
          }
          onExit();
        },
      });

      // Store the instance both in ref and globally (for cleanup)
      tellerConnectInstanceRef.current = tellerConnect;
      (window as any).__tellerConnectInstance = tellerConnect;
      isInitializedRef.current = true;

      // Open Teller Connect
      tellerConnect.open();
    } catch (error: any) {
      console.error('Error setting up Teller Connect:', error);
      
      // Check if error is about multiple instances
      if (error.message && error.message.includes('single Teller Connect embed')) {
        toast({
          title: 'Error',
          description: 'Teller Connect is already open. Please close the existing connection and try again.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to initialize Teller Connect. Please refresh the page and try again.',
          variant: 'destructive',
        });
      }
      
      isInitializedRef.current = false;
      tellerConnectInstanceRef.current = null;
      if ((window as any).__tellerConnectInstance) {
        delete (window as any).__tellerConnectInstance;
      }
      onExit();
    }

    // Cleanup function
    return () => {
      // Reset the flag when component unmounts
      isInitializedRef.current = false;
      if (tellerConnectInstanceRef.current) {
        try {
          if (typeof tellerConnectInstanceRef.current.destroy === 'function') {
            tellerConnectInstanceRef.current.destroy();
          } else if (typeof tellerConnectInstanceRef.current.close === 'function') {
            tellerConnectInstanceRef.current.close();
          }
        } catch (error) {
          console.warn('Error cleaning up Teller Connect on unmount:', error);
        }
        tellerConnectInstanceRef.current = null;
      }
      // Clean up global instance
      if ((window as any).__tellerConnectInstance) {
        delete (window as any).__tellerConnectInstance;
      }
    };
    // Only depend on config values - these should be stable once set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.applicationId, config.products]);

  const getAccountIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'depository':
        return '🏦';
      case 'credit':
        return '💳';
      default:
        return '💰';
    }
  };

  const formatAccountType = (type: string, subtype?: string) => {
    if (subtype) {
      return subtype.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Account';
  };


  return (
    <>
      <Dialog 
        open={showAccountSelection} 
        onOpenChange={(open) => {
          if (open) {
            // Modal is opening - notify parent to hide overlay
            if (onAccountSelectionModalOpen) {
              onAccountSelectionModalOpen();
            }
          } else if (!saving) {
            // Modal is closing
            setShowAccountSelection(false);
            onExit();
          }
        }}
      >
        <DialogContent className="sm:max-w-lg z-[100]">
          <DialogHeader className="space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
            <DialogTitle className="text-center text-xl">Bank Connected Successfully!</DialogTitle>
            <DialogDescription className="text-center">
              We found {tellerAccounts.length} account{tellerAccounts.length > 1 ? 's' : ''} linked to your bank.
              <br />
              <span className="font-medium text-foreground">Please select which account to use for transaction syncing:</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4 max-h-[300px] overflow-y-auto pr-1">
            {tellerAccounts.map((account) => (
              <Card
                key={account.id}
                className={`cursor-pointer border-2 transition-all hover:border-primary hover:shadow-md ${saving ? 'opacity-50 pointer-events-none' : ''}`}
                onClick={() => !saving && handleAccountSelect(account.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="text-2xl">
                      {getAccountIcon(account.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold truncate">{account.name}</h4>
                        {account.last_four && (
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            ••••{account.last_four}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {account.institution?.name} • {formatAccountType(account.type, account.subtype)}
                      </p>
                    </div>
                    <CreditCard className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {saving && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2">
              <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
              <span>Connecting account...</span>
            </div>
          )}
          
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={onExit} disabled={saving}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

